import {
	type FontEntry,
	type SDK_Colors,
	type SDK_Filter,
	type SDK_Fonts,
	type SDK_GetFilterQuery,
	type SsrData,
	checkSizesAreNumbers,
	clientConfig,
	filterTemplate,
	strings,
	systemFonts,
} from "@quickpoint.me/shared";
import {
	type AtomEffect,
	atom,
	atomFamily,
	selector,
	selectorFamily,
} from "recoil";
import {
	sendFiltersAreLoadedAlertToAuthor,
	sendInterpolatedColorsToAuthor,
} from "../functionalComponents/MessageHandler";
import { NeuroDivergentColorChoice, UseNDRColors } from "./accessibility";
import { IsEmbedded, IsInAuthor, IsPreviewGenerator } from "./ui";

import type { FilterInterface } from "../@types/viewerati";
import interpolateColors from "./interpolateColors";
import interpolateFontSizes from "./interpolateFontSizes";
import interpolateFonts from "./interpolateFonts";
import ndrColorsOverride from "./ndrColorsOverride";
import { PageIndex } from "./page";
import { ParsedCode } from "./selectors";

declare const window: {
	__SSR_DATA: SsrData | undefined;
	__SSR_FONT_DATA: FontEntry[] | undefined;
} & Window;

function base64Decode(string: string) {
	const encString = atob(string);
	const length = encString.length;
	const buf = new ArrayBuffer(length);
	const bufView = new Uint8Array(buf);
	for (let i = 0; i < length; i++) {
		bufView[i] = encString.charCodeAt(i);
	}
	return buf;
}

function bufferToBase64(buffer: ArrayBuffer) {
	let binary = "";
	const bytes = new Uint8Array(buffer);
	const len = bytes.byteLength;
	for (let i = 0; i < len; i++) {
		binary += String.fromCharCode(bytes[i]);
	}
	return window.btoa(binary);
}


// Convenience selector for filter
export const CurrentFilter = selector<SDK_Filter>({
	key: "VS_CurrentFilter",
	get: ({ get }) => {
		const filterName = get(FilterName);
		const filter = get(FilterSelector(filterName));
		return filter;
	},
});

// a filter sent via the messaging system from the editor
export const InjectedFilter = atom<null | SDK_Filter>({
	key: "VS_InjectedFilter",
	default: null,
});

export const FilterNames = atom<string[]>({
	key: "VS_FilterNames",
	default: [],
});

export const FilterName = selector<string>({
	key: "VS_FilterName",
	get: ({ get }) => {
		const pageIdx = get(PageIndex);
		const parsedCode = get(ParsedCode);
		// Search for the last "filter:" element up to the current page index
		const allFound = [];
		for (let idx = 0; idx < pageIdx; idx++) {
			const page = parsedCode[idx];
			if (!page) return strings.defaultFilter;
			const found = page.find((el) => el && el.type === "filter:");

			if (found) {
				allFound.push(found.content);
			}
		}
		if (allFound.length > 0) return allFound[allFound.length - 1];

		// Fallback to default filter name if none found
		return strings.defaultFilter;
	},
	cachePolicy_UNSTABLE: {
		// Only store the most recent set of dependencies and their values
		eviction: "most-recent",
	},
});

function appendFontToPreview(font: string, fontData: ArrayBuffer): string {
	const extension = font.split(".").pop()?.toLowerCase() || "ttf";
	const mimeType = extension === "svg" ? "image/svg+xml" : `font/${extension}`;
	const base64 = bufferToBase64(fontData);
	const url = `data:${mimeType};base64,${base64}`;

	const fontCss = document.createElement("style");
	const fontCssRule = `
                @font-face {
                font-family: "${font}";
                src: url("${url}");
              }`;

	fontCss.appendChild(document.createTextNode(fontCssRule));
	document.head.appendChild(fontCss);
	return fontCssRule;
}

function quoteFontName(font: string, _type: "preview" | "QP"): string {
	// Font family name in FontFace must match CSS font-family value exactly
	// CSS uses: font-family: "FontName" (quotes are CSS syntax, not part of name)
	// FontFace needs: new FontFace("FontName", ...) (no extra quotes)
	// Adding quotes to the name creates a mismatch: registered as '"FontName"' but CSS looks for 'FontName'
	return font;
}

// loadFonts takes an object of keys and names and creates a promise.all that waits until each font has loaded
async function loadFonts(
	fonts: SDK_Fonts,
	type: "preview" | "QP",
): Promise<{ loaded: string[]; css: string[] }> {
	const loaded: string[] = [];
	const css: string[] = [];
	const keys = Object.keys(fonts);
	const ssrFonts = window.__SSR_FONT_DATA;


	for (const key of keys) {
		// @ts-expect-error: TODO: type this
		const font = fonts[key];
		if (!font) continue;

		const loadFont = async (): Promise<string | undefined> => {
			// resolve system fonts immediately
			if (systemFonts.includes(font)) {
				return font;
			}

			// use any fonts embedded by SSR
			if (ssrFonts && ssrFonts.length > 0) {
				const ssrFont = ssrFonts.find((f) => f?.fontName === font);
				if (ssrFont) {
					const fontName = quoteFontName(font, type);

					const fontData = base64Decode(ssrFont.fontData);

					const newFont = new FontFace(fontName, fontData);

					await newFont.load();

					document.fonts.add(newFont);
					return font;
				}
			}

			// load any fonts required. Only used when Viewer is in the Author
			const url = `${clientConfig.getFont()}/${font}`;
			const fontContent = await fetch(url).then((r) => r.arrayBuffer());
			const fontName = quoteFontName(font, type);

			const newFont = new FontFace(fontName, fontContent);
			await newFont.load();
			document.fonts.add(newFont);
			if (type === "preview") {
				const style = appendFontToPreview(font, fontContent);
				css.push(style);
			}
			return font;
		};

		try {
			const result = await loadFont();
			if (result) loaded.push(result);
		} catch (e) {
			console.error(`[FontLoader] Failed to load font "${font}":`, e);
		}
	}

	return { loaded, css };
}

// Lazy load GraphQL SDK only when needed (not during SSR hydration)
async function getGraphQLSdk() {
	const [{ GraphQLClient }, { getSdk }] = await Promise.all([
		import("graphql-request"),
		import("@quickpoint.me/shared")
	]);
	const client = new GraphQLClient(clientConfig.gqlGetFilter());
	return getSdk(client);
}

// get any filters from data injected by the server

export const loadFilter =
	(filterJson: string): AtomEffect<FilterInterface> =>
		({ trigger, setSelf }) => {
			const { ownerName, filterName, type: jsonType } = JSON.parse(filterJson);
			const type = jsonType || "QP";
			let res: SDK_GetFilterQuery;

			const ssrFilters = window.__SSR_DATA?.filterData;
			const load = async () => {
				try {
					if (ssrFilters) {
						const ssrFilterName = `${ownerName}::${filterName}`;
						const ssrFilter = ssrFilters.find(
							(f) => f?.filterName === ssrFilterName,
						);

						if (ssrFilter) {
							res = { getFilter: JSON.parse(ssrFilter.filter) };
						} else {
							const sdk = await getGraphQLSdk();
							res = await sdk.GetFilter({
								ownerName,
								filterName,
							});
						}
					} else {
						const sdk = await getGraphQLSdk();
						res = await sdk.GetFilter({
							ownerName,
							filterName,
						});
					}

					if (res.getFilter === null) throw new TypeError("Filter is null");
					let filter = res.getFilter as FilterInterface;
					filter = interpolateColors(filter);
					filter = interpolateFontSizes(filter);
					filter = interpolateFonts(filter);

					checkSizesAreNumbers(filter);

					// inject fonts
					try {
						const { css } = await loadFonts(filter.fonts, type);
						const cssString = css.join("\n");
						sendFiltersAreLoadedAlertToAuthor(type, cssString);
					} catch (e) {
						//console.log(e);
						sendFiltersAreLoadedAlertToAuthor(type);
						e;
					}
					setSelf(filter);
				} catch (error) {
					console.error(error);
					const template = structuredClone(filterTemplate) as FilterInterface;
					sendFiltersAreLoadedAlertToAuthor(type);
					setSelf(interpolateColors(template));
				}
			};

			if (trigger === "get") {
				load();
			}
		};

export const FilterAtomFamily = atomFamily<FilterInterface, string>({
	key: "VS_Filter",
	default: () => {
		let defaultFilter = structuredClone(filterTemplate) as FilterInterface;
		defaultFilter = interpolateColors(defaultFilter);
		defaultFilter = interpolateFontSizes(defaultFilter);
		return defaultFilter;
	},
	effects: (filterJson) => [loadFilter(filterJson)],
});

export const FilterSelector = selectorFamily<FilterInterface, string>({
	key: "VS_Filter",
	get:
		() =>
			async ({ get }) => {
				const filter = get(FilterName);
				const filterNames = get(FilterNames);
				const isInAuthor = get(IsInAuthor);
				const isEmbedded = get(IsEmbedded);
				const type = get(IsPreviewGenerator) ? "preview" : "QP";
				const [ownerName, filterName] = filter.split("::");
				const useNDRColors = get(UseNDRColors);
				const ndrColors = get(NeuroDivergentColorChoice);

				let defaultFilter = structuredClone(filterTemplate) as FilterInterface;
				defaultFilter = interpolateColors(defaultFilter);
				defaultFilter = interpolateFontSizes(defaultFilter);
				defaultFilter = interpolateFonts(defaultFilter);
				const defaultFontsPromise = loadFonts(defaultFilter.fonts, type);

				if (useNDRColors)
					defaultFilter = ndrColorsOverride({
						filter: defaultFilter,
						background: ndrColors.background,
						text: ndrColors.text,
					});
				if (filter === strings.defaultFilter) {
					defaultFontsPromise.then(({ css }) => {
						sendFiltersAreLoadedAlertToAuthor(type, css.join("\n"));
					});
					return defaultFilter;
				}

				// stop reloads every time user types
				if (!isEmbedded && isInAuthor && !filterNames.includes(filter)) {
					defaultFontsPromise.then(({ css }) => {
						sendFiltersAreLoadedAlertToAuthor(type, css.join("\n"));
					});
					return defaultFilter;
				}
				const filterJson = JSON.stringify({ filterName, ownerName, type });
				let f = get(FilterAtomFamily(filterJson));
				if (useNDRColors)
					f = ndrColorsOverride({
						filter: structuredClone(f) as FilterInterface,
						background: ndrColors.background,
						text: ndrColors.text,
					});
				return f;
			},
});
export const FilterColors = selector<SDK_Colors>({
	key: "VS_FilterColors",
	get: ({ get }) => {
		const filter = get(CurrentFilter);
		sendInterpolatedColorsToAuthor("QP", filter.colors);
		return filter.colors;
	},
});

export const FilterFonts = selector<SDK_Fonts>({
	key: "VS_FilterFonts",
	get: ({ get }) => {
		const filter = get(CurrentFilter);
		return filter.fonts;
	},
});

export const FontsAreLoaded = selector<boolean>({
	key: "VS_FontsAreLoaded",
	get: ({ get }) => {
		const filterName = get(FilterName);
		const filter = get(FilterSelector(filterName));
		if (filter.filterName === strings.defaultFilter) {
			return true;
		}

		if (filter.filterName !== strings.defaultFilter) {
			return true;
		}
		return false;
	},
});
